import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveState } from "@/lib/useLiveState";
import { Radio, Table2, Gauge, Image, Settings, ExternalLink, LogOut, Wifi, WifiOff, Megaphone, FolderKanban, CalendarDays, Users } from "lucide-react";
import TablesTab from "@/components/admin/TablesTab";
import ScoreboardTab from "@/components/admin/ScoreboardTab";
import MediaTab from "@/components/admin/MediaTab";
import SettingsTab from "@/components/admin/SettingsTab";
import TickerTab from "@/components/admin/TickerTab";
import ProjectsTab from "@/components/admin/ProjectsTab";
import { BACKEND_URL } from "@/lib/api";

const TABS = [
  { id: "projects", label: "Projekte", icon: FolderKanban },
  { id: "scoreboard", label: "Anzeigetafel", icon: Gauge },
  { id: "schedule", label: "Spielplan", icon: CalendarDays },
  { id: "standings", label: "Tabelle", icon: Table2 },
  { id: "media", label: "Werbung", icon: Image },
  { id: "teams", label: "Mannschaften", icon: Users },
  { id: "ticker", label: "Live-Ticker", icon: Megaphone },
  { id: "settings", label: "Einstellungen", icon: Settings },
];

export default function Admin() {
  const { snapshot, connected } = useLiveState();
  const { logout } = useAuth();
  const [tab, setTab] = useState("scoreboard");
  const [title, setTitle] = useState("");
  const activeProjectId = snapshot?.active_project_id;

  // keep title input in sync with the active project's event title
  useEffect(() => {
    if (snapshot?.settings?.event_title != null) {
      setTitle(snapshot.settings.event_title);
    }
    // eslint-disable-next-line
  }, [activeProjectId, snapshot?.settings?.event_title]);

  const switchProject = async (pid) => {
    if (!pid || pid === activeProjectId) return;
    try {
      await api.post(`/projects/${pid}/activate`);
      const p = (snapshot.projects || []).find((x) => x.id === pid);
      toast.success(`Projekt geöffnet: ${p?.name || ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const publishSchedule = async () => {
    try {
      await api.post("/schedule/publish");
      toast.success("Spielplan freigegeben");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveTitle = async () => {
    try {
      await api.put("/settings", { event_title: title });
      toast.success("Titel gespeichert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!snapshot) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] text-[#8A99AD] font-display uppercase tracking-widest">
        Lade Steuerung…
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#0A0C10] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#0A0C10]/95 backdrop-blur border-b border-[#1E2638]">
        <div className="px-3 sm:px-6 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-lg bg-[#E5FF00] flex items-center justify-center">
              <Radio className="w-5 h-5 text-black" />
            </div>
            <span className="font-display font-black text-xl uppercase tracking-wide hidden sm:block">
              LED<span className="text-[#E5FF00]">Board</span>
            </span>
          </div>

          <div className="flex items-center gap-2 bg-[#121620] border border-[#1E2638] rounded-lg px-2 py-1.5 shrink-0" title="Aktives Projekt">
            <FolderKanban className="w-4 h-4 text-[#E5FF00]" />
            <select value={activeProjectId || ""} onChange={(e) => switchProject(e.target.value)} data-testid="project-switcher"
              className="bg-transparent text-sm font-display font-bold uppercase tracking-wide text-white outline-none max-w-[160px] sm:max-w-[220px] cursor-pointer">
              {(snapshot.projects || []).map((p) => (
                <option key={p.id} value={p.id} className="bg-[#121620] text-white normal-case">{p.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <span className="flex items-center" title={connected ? "Verbunden" : "Getrennt"}>
              {connected ? <Wifi className="w-4 h-4 text-[#00F0FF]" /> : <WifiOff className="w-4 h-4 text-[#FF2A55]" />}
            </span>
            <a href={`${window.location.origin}/display`} target="_blank" rel="noreferrer" data-testid="open-display-link"
              className="flex items-center gap-1.5 bg-[#00F0FF] text-black font-display font-bold uppercase tracking-wider text-sm px-3 py-2 rounded-lg hover:bg-white transition-colors">
              <ExternalLink className="w-4 h-4" /> <span className="hidden sm:inline">Display</span>
            </a>
            <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg bg-[#121620] border border-[#1E2638] text-[#8A99AD] hover:text-[#FF2A55] transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>

          <div className="w-full order-last">
            <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} placeholder="Veranstaltungstitel" data-testid="event-title-input"
              className="w-full bg-[#121620] border border-[#1E2638] rounded-lg px-4 py-2 outline-none focus:border-[#00F0FF] transition-colors text-sm" />
          </div>
        </div>

        <nav className="flex items-center gap-1 px-2 sm:px-6 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 font-display font-bold uppercase tracking-wider text-xs sm:text-sm px-3 sm:px-4 py-3 border-b-2 transition-colors shrink-0 whitespace-nowrap ${
                  tab === t.id ? "border-[#E5FF00] text-white" : "border-transparent text-[#8A99AD] hover:text-white"
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      {(snapshot.pending_schedule || []).length > 0 && (
        <div className="bg-[#E5FF00] text-black px-3 sm:px-6 py-3 flex flex-wrap items-center gap-3" data-testid="pending-schedule-banner">
          <span className="font-display font-bold uppercase tracking-wider text-sm">
            {snapshot.pending_schedule.length === 1 ? "1 neuer Spielplaneintrag" : `${snapshot.pending_schedule.length} neue Spielplaneinträge`} – freigeben?
          </span>
          <span className="text-xs sm:text-sm truncate flex-1 min-w-[120px]">
            {snapshot.pending_schedule.map((p) => p.label).join("  ·  ")}
          </span>
          <button onClick={publishSchedule} data-testid="publish-schedule-btn" className="bg-black text-[#E5FF00] font-display font-bold uppercase tracking-wider text-sm px-4 py-2 rounded-lg hover:bg-[#121620] transition-colors">
            Freigeben
          </button>
        </div>
      )}

      <main className="p-3 sm:p-6 max-w-[1400px] mx-auto">
        {tab === "projects" && <ProjectsTab snapshot={snapshot} />}
        {tab === "scoreboard" && <ScoreboardTab snapshot={snapshot} />}
        {tab === "schedule" && <TablesTab snapshot={snapshot} view="schedule" />}
        {tab === "standings" && <TablesTab snapshot={snapshot} view="standings" />}
        {tab === "media" && <MediaTab snapshot={snapshot} />}
        {tab === "teams" && <TablesTab snapshot={snapshot} view="teams" />}
        {tab === "ticker" && <TickerTab snapshot={snapshot} />}
        {tab === "settings" && <SettingsTab snapshot={snapshot} />}
      </main>
    </div>
  );
}
